/**
 * Utilitários para detecção de formatos de coordenadas e necessidades de inversão
 */

/**
 * Extrai uma coordenada de amostra de uma geometria para teste
 * @param {Object} geometry - Objeto de geometria GeoJSON
 * @returns {Array|null} - Coordenada de amostra ou null se não for possível extrair
 */
export function getSampleCoordinate(geometry) {
  if (!geometry || !geometry.coordinates || !geometry.type) return null;

  try {
    switch (geometry.type) {
      case 'Point':
        return geometry.coordinates;
      case 'LineString':
        return geometry.coordinates[0];
      case 'Polygon':
        return geometry.coordinates[0][0];
      case 'MultiPolygon':
        return geometry.coordinates[0][0][0];
      default:
        return null;
    }
  } catch (error) {
    console.error('[GeometryService] Erro ao extrair amostra de coordenada:', error);
    return null;
  }
}

/**
 * Extrai uma coordenada de exemplo de um GeoJSON para diagnóstico
 * @param {Object} geojson - Objeto GeoJSON
 * @returns {Array|null} - Uma coordenada de exemplo ou null
 */
export function getSampleCoordinateForDiagnostic(geojson) {
  try {
    if (!geojson || !geojson.coordinates) return null;

    switch (geojson.type) {
      case 'Point':
        return geojson.coordinates;
      case 'LineString':
        return geojson.coordinates[0];
      case 'Polygon':
        return geojson.coordinates[0][0];
      case 'MultiPolygon':
        return geojson.coordinates[0][0][0];
      default:
        return null;
    }
  } catch (error) {
    console.error('[GeometryService] Erro ao extrair amostra para diagnóstico:', error);
    return null;
  }
}

/**
 * Detecta se uma geometria precisa ser invertida com base em testes de coordenadas
 * @param {Object} geometry - Objeto de geometria GeoJSON
 * @returns {boolean} - true se a geometria precisa de inversão, false caso contrário
 */
export function needsCoordinateInversion(geometry) {
  if (!geometry || !geometry.coordinates) return false;

  // Extrai uma amostra de coordenadas para testar
  const sample = getSampleCoordinate(geometry);
  if (!sample || sample.length < 2) return false;

  // Desempacota coordenadas da amostra
  const [x, y] = sample;

  // Se as coordenadas estiverem invertidas, a "longitude" (posição 0) estará na faixa de latitude (-90 a 90)
  // e a "latitude" (posição 1) na faixa de longitude (-180 a 180)
  const mayBeInverted = Math.abs(x) <= 90 && Math.abs(y) <= 180 && Math.abs(y) > 90;

  // Se parece invertido OU se o centroid resultante estaria fora do Brasil
  // (usando o mesmo teste existente no isCoordInBrazil)
  if (mayBeInverted) {
    return true;
  } else if (!isInBrazilRange(x, y) && isInBrazilRange(y, x)) {
    return true;
  }

  return false;
}

/**
 * Verifica rápido se uma coordenada está no range aproximado do Brasil
 * (usado para detectar possíveis inversões)
 * @param {number} lon - Longitude
 * @param {number} lat - Latitude
 * @returns {boolean} - Se está na região aproximada do Brasil
 */
export function isInBrazilRange(lon, lat) {
  // Limites aproximados do Brasil
  const lonRange = [-74, -34]; // oeste/leste
  const latRange = [-33, 5]; // sul/norte

  return lon >= lonRange[0] && lon <= lonRange[1] && lat >= latRange[0] && lat <= latRange[1];
}

/**
 * Verifica se uma coordenada está dentro dos limites geográficos do Brasil
 * @param {number} lon - Longitude
 * @param {number} lat - Latitude
 * @returns {boolean} - Verdadeiro se a coordenada está dentro dos limites do Brasil
 */
export function isCoordInBrazil(lon, lat) {
  // Verifica se a coordenada é válida
  if (!isValidCoordinate(lon, lat)) return false;

  // Limites aproximados do Brasil (com margem)
  const lonRange = [-75, -33]; // oeste/leste
  const latRange = [-35, 6]; // sul/norte

  return lon >= lonRange[0] && lon <= lonRange[1] && lat >= latRange[0] && lat <= latRange[1];
}

/**
 * Verifica se uma coordenada é válida (está dentro dos limites razoáveis)
 * @param {number} lon - Longitude
 * @param {number} lat - Latitude
 * @returns {boolean} - Se a coordenada é válida
 */
function isValidCoordinate(lon, lat) {
  // Verifica se são números
  if (typeof lon !== 'number' || typeof lat !== 'number') return false;
  if (isNaN(lon) || isNaN(lat)) return false;

  // Verifica se estão dentro dos limites globais
  if (Math.abs(lon) > 180 || Math.abs(lat) > 90) return false;

  return true;
}
